import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getMinistries,
  createMinistry,
  updateMinistry,
  deleteMinistry,
} from '../services/api';
import './AdminMinistries.css';

const EMPTY_FORM = {
  name: '',
  leader: '',
  meeting_time: '',
  meeting_location: '',
  contact_email: '',
  image_url: '',
  description: '',
};

export default function AdminMinistries() {
  const [ministries, setMinistries] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const formRef = useRef(null);

  useEffect(() => { load(); }, []);

  function load() {
    getMinistries().then(setMinistries).catch(() => setError('Impossible de charger les ministères.'));
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function startEdit(m) {
    setEditingId(m.id);
    setForm({
      name: m.name || '',
      leader: m.leader || '',
      meeting_time: m.meeting_time || '',
      meeting_location: m.meeting_location || '',
      contact_email: m.contact_email || '',
      image_url: m.image_url || '',
      description: m.description || '',
    });
    setError('');
    setSuccess('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return setError('Le nom du ministère est requis.');
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await updateMinistry(editingId, form);
        setSuccess('Ministère mis à jour.');
      } else {
        await createMinistry(form);
        setSuccess('Ministère ajouté.');
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch {
      setError('Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(m) {
    if (!window.confirm(`Supprimer « ${m.name} » ?`)) return;
    try {
      await deleteMinistry(m.id);
      setSuccess('Ministère supprimé.');
      load();
    } catch {
      setError('Erreur lors de la suppression.');
    }
  }

  return (
    <div className="am-page">
      {/* Header */}
      <div className="am-header">
        <div className="container">
          <div className="am-header__inner">
            <div>
              <Link to="/gestion-ecclesiale" className="am-breadcrumb">← Tableau de bord</Link>
              <h1 className="am-title">Gestion des Ministères</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container am-body">

        {/* ── Form ── */}
        <section className="am-form-section" ref={formRef}>
          <h2 className="am-section-title">
            {editingId ? 'Modifier le ministère' : 'Ajouter un ministère'}
          </h2>

          {error && <div className="am-alert am-alert--error">{error}</div>}
          {success && <div className="am-alert am-alert--success">{success}</div>}

          <form className="am-form" onSubmit={handleSubmit} noValidate>
            <div className="am-row">
              <div className="am-field am-field--grow">
                <label>Nom du ministère *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="ex: Ministère de Jeunesse" />
              </div>
              <div className="am-field am-field--grow">
                <label>Responsable</label>
                <input name="leader" value={form.leader} onChange={handleChange} placeholder="Nom du responsable" />
              </div>
            </div>

            <div className="am-row">
              <div className="am-field am-field--grow">
                <label>Horaire des réunions</label>
                <input name="meeting_time" value={form.meeting_time} onChange={handleChange} placeholder="ex: Samedi à 16h00" />
              </div>
              <div className="am-field am-field--grow">
                <label>Lieu des réunions</label>
                <input name="meeting_location" value={form.meeting_location} onChange={handleChange} placeholder="ex: Salle des Jeunes" />
              </div>
              <div className="am-field am-field--grow">
                <label>Email de contact</label>
                <input type="email" name="contact_email" value={form.contact_email} onChange={handleChange} placeholder="ex: jeunesse@eglise.ht" />
              </div>
            </div>

            {/* Image URL + preview */}
            <div className="am-row am-row--align-start">
              <div className="am-field am-field--grow">
                <label>Image (URL)</label>
                <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." />
              </div>
              {form.image_url && (
                <div className="am-img-preview">
                  <img src={form.image_url} alt="Aperçu" />
                </div>
              )}
            </div>

            <div className="am-field">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Décrivez l'objectif et les activités du ministère..." />
            </div>

            <div className="am-form-actions">
              <button type="submit" className="am-btn am-btn--primary" disabled={saving}>
                {saving ? 'Enregistrement...' : editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              {editingId && (
                <button type="button" className="am-btn am-btn--ghost" onClick={cancelEdit}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ── Table ── */}
        <section className="am-list-section">
          <h2 className="am-section-title">Ministères ({ministries.length})</h2>
          {ministries.length === 0 ? (
            <p className="am-empty">Aucun ministère enregistré.</p>
          ) : (
            <div className="am-table-wrap">
              <table className="am-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Nom</th>
                    <th>Responsable</th>
                    <th>Horaire</th>
                    <th>Lieu</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ministries.map((m) => (
                    <tr key={m.id} className={editingId === m.id ? 'am-row--editing' : ''}>
                      <td>
                        {m.image_url ? (
                          <img src={m.image_url} alt="" className="am-thumb" />
                        ) : (
                          <div className="am-thumb am-thumb--placeholder">🏛️</div>
                        )}
                      </td>
                      <td className="am-td-name">{m.name}</td>
                      <td>{m.leader || '—'}</td>
                      <td className="am-td-muted">{m.meeting_time || '—'}</td>
                      <td className="am-td-muted">{m.meeting_location || '—'}</td>
                      <td>
                        <button className="am-action-btn am-action-btn--edit" onClick={() => startEdit(m)}>Modifier</button>
                        <button className="am-action-btn am-action-btn--delete" onClick={() => handleDelete(m)}>Supprimer</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
